/* Get Insights — state machine. Selection changes move the panel to "idle"
 * (nothing sent to the backend yet); pressing "Get Insights" extracts the
 * shape content and POSTs /content/get-insights. A new selection while a
 * request is in flight aborts it and returns to "idle" for the new target. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import type { GetInsightsState, InsightsSelectionContext } from "../../../types";
import { extractSelectionContent } from "../services/insightsExtractor";
import {
  INSIGHTS_REQUEST_TIMEOUT_MS,
  describeInsightsFailure,
  fetchInsights,
  isInsightsAbortError,
  logInsightsError,
} from "../services/insightsApi";
import { useInsightsSelection } from "./useInsightsSelection";

const GENERIC_ERROR_MESSAGE = "Couldn't generate insights for this selection. Please try again.";

// Identifies "the same shape still selected" so re-detections that resolve
// to an equivalent selection (e.g. a debounce tick while nothing changed)
// don't retrigger analysis — only a genuinely different slide/shape does.
function selectionKey(selection: InsightsSelectionContext | null): string | null {
  return selection ? `${selection.slideIndex}:${selection.shapeId}` : null;
}

export interface UseGetInsightsReturn {
  state: GetInsightsState;
  analyze: () => void;
  retry: () => void;
  cancel: () => void;
}

export function useGetInsights(): UseGetInsightsReturn {
  const { selection } = useInsightsSelection();
  const http = useDeckorateHttp();
  const [state, setState] = useState<GetInsightsState>({ status: "empty" });

  // Bumped on every new analysis run so a slow request that resolves after
  // the selection has already moved on can't overwrite newer state.
  const runIdRef = useRef(0);
  // Cancels the in-flight request on timeout, on a superseding selection
  // change, or on unmount — a stale request should never keep running once
  // its result can no longer be shown.
  const abortRef = useRef<AbortController | null>(null);
  // Distinguishes "our deadline elapsed" from "the selection moved on",
  // since both surface as an AbortError but deserve different copy.
  const timedOutRef = useRef(false);
  // Set on unmount so a late resolution doesn't call setState on a dead component.
  const isMountedRef = useRef(true);

  const safeSetState = useCallback((next: GetInsightsState) => {
    if (isMountedRef.current) setState(next);
  }, []);

  const abortInFlight = useCallback(() => {
    timedOutRef.current = false;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortInFlight();
    };
  }, [abortInFlight]);

  const runAnalysis = useCallback(
    (target: InsightsSelectionContext) => {
      abortInFlight();
      const runId = ++runIdRef.current;
      safeSetState({ status: "loading", selection: target });

      const controller = new AbortController();
      abortRef.current = controller;
      timedOutRef.current = false;
      const timeoutId = setTimeout(() => {
        timedOutRef.current = true;
        controller.abort();
      }, INSIGHTS_REQUEST_TIMEOUT_MS);

      void (async () => {
        try {
          const content = await extractSelectionContent(target);
          if (runId !== runIdRef.current) return; // selection moved on — skip the backend call entirely
          // "slide" is a client-only selection kind (nothing selected → whole
          // slide captured as an image) — the backend only knows the content
          // kinds it can actually receive, so report this one as "image".
          const selectionType = target.type === "slide" ? "image" : target.type;
          const insights = await fetchInsights(
            http,
            { selectionType, slideIndex: target.slideIndex, content },
            { signal: controller.signal },
          );
          if (runId !== runIdRef.current) return;
          safeSetState({ status: "ready", selection: target, insights });
        } catch (error) {
          if (runId !== runIdRef.current) return;
          // A superseded run's abort is expected, not a failure — the newer
          // run already owns the UI state, so this one has nothing to show.
          if (isInsightsAbortError(error) && !timedOutRef.current) return;

          const timedOut = timedOutRef.current;
          logInsightsError("insights-analyze", error);
          safeSetState({
            status: "error",
            selection: target,
            message: describeInsightsFailure(error, GENERIC_ERROR_MESSAGE, { timedOut }),
          });
        } finally {
          clearTimeout(timeoutId);
          if (abortRef.current === controller) abortRef.current = null;
        }
      })();
    },
    [http, safeSetState, abortInFlight],
  );

  // Collapses debounce ticks that resolve to the same shape into a single
  // state transition — only a genuinely different slide/shape moves the
  // panel back to "idle" (analysis itself only ever starts via `analyze`).
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = selectionKey(selection);
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    // A new selection invalidates whatever was previously running/shown for
    // the prior target, whether or not that run ever got started.
    runIdRef.current++;
    abortInFlight();
    safeSetState(selection ? { status: "idle", selection } : { status: "empty" });
  }, [selection, abortInFlight, safeSetState]);

  const analyze = useCallback(() => {
    if (selection) runAnalysis(selection);
  }, [selection, runAnalysis]);

  const retry = analyze;

  // Drops the in-flight request and returns to "idle" for the same
  // selection, so the user can re-press "Get Insights" without reselecting.
  const cancel = useCallback(() => {
    if (state.status !== "loading") return;
    runIdRef.current++; // invalidate the in-flight run so its late resolution is ignored
    abortInFlight();
    safeSetState({ status: "idle", selection: state.selection });
  }, [state, abortInFlight, safeSetState]);

  return { state, analyze, retry, cancel };
}
